import { useCallback, useState, useEffect, useMemo } from 'react'
import { z } from 'zod'
import { errors } from '~/prix'
import { BaseActionDefinitionEnhanced } from '~/prix/actionDefinition'
import useAPI from './api'
import objectHash from 'object-hash'
import { get, set } from '~/prix/utils/browserCache'
import { GenericObject } from '~/prix/types/type'
import { isDevelopment } from '~/utils/shared/platform'
import useRespectiveState from './respectiveState'

export interface ActionCall<
  InputType extends z.ZodType<any, any> = z.ZodType<any, any>,
  OutputType extends z.ZodType<any, any> = z.ZodType<any, any>,
> {
  actionDefinition: BaseActionDefinitionEnhanced<InputType, OutputType>
  input: z.infer<InputType>
  options?: {
    staleWhileRevalidate?: boolean
    sourceKey?: string
  }
}

export type ActionState<Output> = {
  result: Output | undefined
  error: Error | null
  isLoading: boolean
}

type ActionStates<ActionCalls extends ActionCall<any, any>[]> = {
  [K in keyof ActionCalls]: ActionCalls[K] extends ActionCall<any, infer OutputType>
    ? ActionState<z.infer<OutputType>>
    : never
}

export default function useConcurrentActions<ActionCalls extends ActionCall<any, any>[]>(
  actions: ActionCalls,
  deps: React.DependencyList,
): {
  someIsLoading: boolean
  someError: Error | null
  states: ActionStates<ActionCalls>
} {
  const [states, setStates] = useRespectiveState(
    () =>
      actions.map(() => ({
        result: undefined,
        error: null,
        isLoading: true,
      })) as ActionState<any>[],
    deps,
  )
  const [someIsLoading, setSomeIsLoading] = useRespectiveState(() => true, deps)
  const [someError, setSomeError] = useRespectiveState(() => null, deps)

  const api = useAPI()

  useEffect(() => {
    setSomeIsLoading(true)
    setSomeError(null)

    const abortControllers = actions.map(() => new AbortController())
    const promises = actions.map((action, index) => {
      if (action === null) {
        setStates(prevResults => {
          const newStates = [...prevResults]
          newStates[index] = {
            error: null,
            isLoading: false,
            result: null,
          }
          return newStates
        })
        return Promise.resolve(null)
      }

      const { actionDefinition, input, options } = action
      const { staleWhileRevalidate = false, sourceKey = 'default' } = options || {}
      const source = api.sources[sourceKey]
      const abortController = abortControllers[index]

      if (!source) {
        throw errors.incompatible('Fonte de dados indisponível')
      }

      // Cache preparation
      const now = +new Date()
      const cache = actionDefinition.cache && !isDevelopment ? actionDefinition.cache(input) : null
      const hash =
        cache &&
        cache.front &&
        (cache.front.expirationMsForUpdate || cache.front.expirationMsForFallback)
          ? objectHash(input as GenericObject, {
              respectType: false,
            })
          : null
      const cacheName = hash ? `${actionDefinition.key}.${hash}` : null

      const cacheResultPromise: Promise<{
        value: any
        timestamp: number
        expirationTimestamp: number
      } | null> = cacheName ? get(cacheName) : Promise.resolve(null)

      const resultPromise = cacheResultPromise.then(cacheResult => {
        if (cacheResult !== null) {
          const isBestCache =
            cacheResult !== null &&
            cache!.front!.expirationMsForUpdate &&
            cacheResult.timestamp + cache!.front!.expirationMsForUpdate > now
          if (staleWhileRevalidate && !isBestCache) {
            console.log('Using stale cache while revalidating', cacheName, input)
            setStates(prevResults => {
              const newStates = [...prevResults]
              newStates[index] = {
                error: null,
                isLoading: false,
                result: cacheResult.value,
              }
              return newStates
            })
          } else if (isBestCache) {
            console.log('Using directly from cache', cacheName, input)
            setStates(prevResults => {
              const newStates = [...prevResults]
              newStates[index] = {
                error: null,
                isLoading: false,
                result: cacheResult.value,
              }
              return newStates
            })
            return cacheResult.value
          }
        }

        const call = source.call(actionDefinition, input, api.context)
        abortController.signal.addEventListener('abort', () => call.abort())
        return call
          .run()
          .then(result => {
            setStates(prevResults => {
              const newStates = [...prevResults]
              newStates[index] = {
                error: null,
                isLoading: false,
                result,
              }
              return newStates
            })
            if (cacheName) {
              console.log('Saving to cache', cacheName, input, result)
              set(
                cacheName,
                result,
                Math.max(
                  cache!.front!.expirationMsForUpdate || 0,
                  cache!.front!.expirationMsForFallback || 0,
                ),
              ).catch(error => console.error(error))
            }
            return result
          })
          .catch(error => {
            if (
              cacheName &&
              cacheResult !== null &&
              cache!.front!.expirationMsForFallback &&
              cacheResult.timestamp + cache!.front!.expirationMsForFallback > now
            ) {
              console.error(error)
              console.log('Using fallback cache', cacheName, input)
              setStates(prevResults => {
                const newStates = [...prevResults]
                newStates[index] = {
                  error: null,
                  isLoading: false,
                  result: cacheResult.value,
                }
                return newStates
              })
              return cacheResult!.value
            }
            setStates(prevResults => {
              const newStates = [...prevResults]
              newStates[index] = {
                error,
                isLoading: false,
                result: null,
              }
              return newStates
            })
            throw error
          })
      })

      return resultPromise
    })

    Promise.all(promises)
      .catch(error => {
        setSomeError(error)
      })
      .finally(() => {
        setSomeIsLoading(false)
      })

    return () => {
      abortControllers.forEach(abortController => abortController.abort())
    }
  }, [api.context, ...deps])

  const returning = useMemo(
    () => ({
      someIsLoading,
      someError,
      states: states as ActionStates<ActionCalls>,
    }),
    [someIsLoading, someError, states],
  )

  return returning
}
